import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { TAX_FAQ_CATEGORIES } from "@/content/tax-corpus/faq-defs";

const ADVICE_EN = [
  "Keep a day-count table for every tax year with copies of stamped passport pages and departure cards — the burden of proof sits with the taxpayer.",
  "Keep pre-2024 savings in a separate account from new income, and retain offshore statements as at 31 December 2023 as your opening evidence.",
  "Read the specific double tax agreement with your payer's country: the pension, dividend and royalty articles differ from treaty to treaty.",
  "Collect the section 50 bis withholding certificate at the time of payment, not at year end, or the tax credit becomes hard to claim.",
  "Build a tax calendar: PP 30 by the 15th monthly, social security contributions, PND 51 at half year, PND 50 within 150 days of year end.",
  "Align the company's accounting period with the parent group at incorporation — changing it later needs Revenue Department approval.",
  "Obtain the 13-digit tax ID and register for e-Filing before filing season; first-time identity verification takes time.",
  "Before translating Thai tax documents, confirm whether the destination requires consular certification, embassy legalisation, or a locally registered translator.",
];

const CAUTIONS_EN = [
  "Assuming visa category decides tax liability, when the law looks at days present in Thailand and the source of the income.",
  "Assuming remote work for a foreign employer is untaxed, when duties performed in Thailand are Thai-source income under section 41.",
  "Remitting large sums with no evidence of source or of when the income arose, making the P.162/2566 carve-out impossible to prove.",
  "Skipping the Thai return because tax was paid abroad — a foreign tax credit must be claimed on a filed return with foreign receipts.",
  "Leaving a dormant company unaudited and unfiled, accumulating fines and blocking work-permit renewals and bank accounts.",
  "Under-estimating half-year net profit on PND 51 by more than 25% without reasonable cause, which triggers a surcharge.",
  "Crossing THB 1.8 million without registering for VAT within 30 days, creating retrospective output VAT plus penalties.",
  "Submitting translated financial statements whose figures or captions do not match the original line by line, forcing a second review round.",
];

export const Route = createFileRoute("/en/tax/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/tax-corpus");
    return { faqs: m.TAX_FAQ_EN };
  },
  component: EnTaxFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Thai Tax & Accounting for Foreigners — ${faqs.length} FAQs`,
        description:
          "Thai tax residency and the 180-day rule, PND 90/91 filing, foreign income under Orders P.161/2566 and P.162/2566, withholding tax, VAT, audited financial statements, double tax agreements and legalising Thai tax documents.",
        path: "/en/tax/faq",
        type: "article",
        alternates: [{ lang: "th", path: "/tax/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: site },
              { name: "Thai Tax & Accounting FAQ", url: `${site}/en/tax/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/tax/faq")) },
      ],
    };
  },
});

function EnTaxFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Thai Tax &amp; Accounting</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Thai Tax and Accounting for Foreigners and Foreign-Owned Companies
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          {faqs.length} answers on tax residency under section 41 of the Revenue Code, PND 90 and PND 91 returns,
          foreign-source income under Revenue Departmental Instructions P.161/2566 and P.162/2566, withholding tax,
          VAT, accounting duties under the Accounting Act B.E. 2543, double tax agreements, and certifying Thai tax
          documents for use abroad.
        </p>

        <section aria-label="Categories" className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Browse by topic</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {TAX_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/tax/faq/${c.slug}`} className="rounded-lg border bg-card p-4 hover:bg-muted">
                <span className="block font-semibold">{c.titleEn}</span>
                <span className="mt-1 block text-xs text-muted-foreground">{c.descEn}</span>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <ServiceAdviceBlock items={ADVICE_EN} locale="en" />
        <ServiceCautionBlock items={CAUTIONS_EN} locale="en" />

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Ask our team to review your position</h2>
          <p className="text-sm text-muted-foreground">
            Tell us your days present in Thailand for the tax year, the income categories, the source country and what
            the receiving authority requires. Phone, LINE or email, Monday to Saturday 9:00–18:00.
          </p>
          <a href="/en/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact our team</a>
        </section>
        <div className="mt-8 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/tax/faq" className="text-primary underline">อ่านหน้านี้เป็นภาษาไทย →</a>
        </div>
        <FaqSourceNote topic="tax" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}